'use client';

import React, { useState, useMemo, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { categories } from '@/lib/data/categories';
import { converters } from '@/lib/converters';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { X, Search, ChevronDown, LucideIcon, Star, Home } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  className?: string
}

export default function Sidebar({ isOpen, onClose, className }: SidebarProps) {
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

  useEffect(() => {
    const loadFavorites = () => {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
      setFavoriteIds(favorites);
    };

    loadFavorites();
    window.addEventListener('favoritesChanged', loadFavorites);
    return () => window.removeEventListener('favoritesChanged', loadFavorites);
  }, []);

  const favoriteConverters = useMemo(() => {
    return favoriteIds
      .map(id => converters[id])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [favoriteIds]);

  const filteredConverters = useMemo(() => {
    if (!searchQuery) return [];

    const query = searchQuery.toLowerCase();
    return Object.values(converters).filter((converter) =>
      converter.name.toLowerCase().includes(query) ||
      converter.units.some(unit =>
        unit.name.toLowerCase().includes(query) ||
        unit.symbol.toLowerCase().includes(query)
      )
    );
  }, [searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prevExpanded) => {
      if (prevExpanded.includes(categoryId)) {
        return prevExpanded.filter((id) => id !== categoryId);
      } else {
        return [...prevExpanded, categoryId];
      }
    });
  };

  const isCategoryActive = (categoryName: string) => {
    return pathname.startsWith(`/${categoryName.toLowerCase().replace(/\s+/g, '-')}`);
  };

  const isConverterActive = (converterName: string) => {
    const nameSlug = converterName.toLowerCase().replace(/\s+/g, '-');
    return pathname.endsWith(`/${nameSlug}`);
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 w-80 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out desktop:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${className || ''}`}
    >
      <div className="p-6 border-b border-gray-200 sticky top-0 bg-white/95 backdrop-blur-sm flex justify-between items-center z-10">
        <Link href="/" className="flex items-center gap-3 text-gray-800 hover:text-emerald-600 transition-colors">
          <div className="bg-emerald-600 p-2 rounded-xl text-white">
            <Icons.Scale className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold tracking-tight">Unit Converter</h1>
        </Link>
        <button
          onClick={onClose}
          className="desktop:hidden p-2 hover:bg-gray-100 rounded-xl"
          aria-label="Close sidebar"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="p-4 border-b bg-gray-50/50">
        <div className="relative">
          <input
            id="sidebar-search"
            type="text"
            placeholder="Search all units..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2.5 border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all duration-200 text-sm"
          />
          <Search className="absolute left-3.5 top-3 text-gray-400 w-4.5 h-4.5" />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-2.5 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <nav className="p-4 overflow-y-auto h-[calc(100vh-140px)] scrollbar-modern space-y-6 pb-16">
        {/* Navigation Section */}
        <div className="space-y-1">
          <Link
            href="/"
            onClick={onClose}
            className={`w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 transition-all ${pathname === '/' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
          >
            <Home className="w-5 h-5" />
            <span className="font-semibold text-sm">Dashboard</span>
          </Link>
        </div>

        {/* Favorites Section */}
        {!searchQuery && (
          <div className="space-y-1">
            <h3 className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              Favorites
            </h3>
            {favoriteConverters.length > 0 ? (
              favoriteConverters.map((converter) => {
                const IconComponent = Icons[converter.icon as keyof typeof Icons] as LucideIcon | undefined;
                const category = categories.find(cat => cat.converters.includes(converter.id));
                const categorySlug = category?.name.toLowerCase().replace(/\s+/g, '-');
                const converterSlug = converter.name.toLowerCase().replace(/\s+/g, '-');

                return (
                  <Link
                    href={`/${categorySlug}/${converterSlug}`}
                    key={`fav-${converter.id}`}
                    onClick={onClose}
                    className={`w-full text-left px-4 py-2.5 rounded-xl flex items-center gap-3 transition-all group ${isConverterActive(converter.name)
                      ? 'bg-amber-50 text-amber-700'
                      : 'text-gray-600 hover:bg-amber-50/50 hover:text-amber-700'
                      }`}
                  >
                    <div className={`p-1.5 rounded-lg transition-colors ${isConverterActive(converter.name) ? 'bg-amber-100' : 'bg-gray-100 group-hover:bg-amber-100'
                      }`}>
                      {IconComponent && <IconComponent className="w-4 h-4" />}
                    </div>
                    <span className="text-sm font-medium">{converter.name}</span>
                  </Link>
                );
              })
            ) : (
              <div className="px-4 py-3 bg-gray-50 rounded-xl border border-dashed border-gray-200 mx-2">
                <p className="text-[10px] text-gray-400 text-center leading-relaxed">
                  Click the <Star className="inline w-2 h-2" /> on any converter to save it here for quick access!
                </p>
              </div>
            )}
          </div>
        )}

        {/* Main Categories */}
        <div className="space-y-1">
          <h3 className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
            Categories
          </h3>
          {searchQuery ? (
            <div className="space-y-1">
              {filteredConverters.length > 0 ? (
                filteredConverters.map((converter) => {
                  const IconComponent = Icons[converter.icon as keyof typeof Icons] as LucideIcon | undefined;
                  const category = categories.find(cat => cat.converters.includes(converter.id));
                  return (
                    <Link
                      href={`/${category?.name.toLowerCase().replace(/\s+/g, '-')}/${converter.name.toLowerCase().replace(/\s+/g, '-')}`}
                      key={converter.id}
                      onClick={onClose}
                      className={`w-full text-left px-4 py-2.5 rounded-xl flex items-center gap-3 transition-all ${isConverterActive(converter.name)
                        ? 'bg-emerald-50 text-emerald-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                      <div className="bg-gray-100 p-1.5 rounded-lg">
                        {IconComponent && <IconComponent className="w-4 h-4 text-emerald-600" />}
                      </div>
                      <span className="text-sm">{converter.name}</span>
                    </Link>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center">
                  <div className="bg-gray-50 rounded-2xl p-4 inline-block mb-3">
                    <Search className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-gray-500 text-xs">
                    No results for "{searchQuery}"
                  </p>
                </div>
              )}
            </div>
          ) : (
            categories.map((category) => {
              const categoryConverters = category.converters
                .map((converterId) => converters[converterId])
                .filter(Boolean)
                .sort((a, b) => a.name.localeCompare(b.name));

              const isExpanded = expandedCategories.includes(category.id);
              const IconComponent = Icons[category.icon as keyof typeof Icons] as LucideIcon | undefined;
              const isActive = isCategoryActive(category.name);

              return (
                <div key={category.id} className="space-y-1">
                  <div className={`flex items-center gap-1 rounded-xl transition-all ${isActive ? 'bg-emerald-50/50' : 'hover:bg-gray-50'
                    }`}>
                    <Link
                      href={`/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={onClose}
                      className={`flex-1 flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'text-emerald-700 font-bold' : 'text-gray-600'
                        }`}
                    >
                      {IconComponent && <IconComponent className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />}
                      <span className="text-sm">{category.name}</span>
                    </Link>
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className={`p-2 mr-1 rounded-lg transition-all ${isExpanded ? 'bg-emerald-100 text-emerald-700' : 'text-gray-400 hover:bg-gray-100'
                        }`}
                      aria-label="Toggle"
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="ml-4 pl-4 border-l-2 border-emerald-100 space-y-1 animate-in slide-in-from-left-2 duration-300">
                      {categoryConverters.map((converter) => (
                        <Link
                          href={`/${category.name.toLowerCase().replace(/\s+/g, '-')}/${converter.name.toLowerCase().replace(/\s+/g, '-')}`}
                          key={converter.id}
                          onClick={onClose}
                          className={`w-full text-left px-4 py-2 rounded-lg flex items-center gap-2 transition-all text-xs font-medium ${isConverterActive(converter.name)
                            ? 'bg-emerald-50 text-emerald-700 font-bold'
                            : 'text-gray-500 hover:text-emerald-600'
                            }`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full ${isConverterActive(converter.name) ? 'bg-emerald-500' : 'bg-gray-300'
                            }`} />
                          {converter.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </nav>
    </aside>
  );
}
